import { useCreateLike } from '@bpm-web-app/swr-hooks';
import { appendQueryParams, CreateCardCarouselItem, DEFAULT_BROWSE_CARD_LIMIT } from '@bpm-web-app/utils';
import { useMemo } from 'react';
import dayjs from 'dayjs';

import { UrlObject } from 'url';
import CardCarousel from '../shared/card-carousel/card-carousel';
import { GhostComponent } from '../shared';
import CardCreate, { CreateCardContentTypeWithoutActions, CreateCardSize } from '../shared/card/card-create';
import NoResultsBlock from '../shared/ui/no-results-block/no-results-block';
import { CreateActionType } from '../shared/three-dots-sheet/create-three-dots-sheet.context';

export interface CreateCardCarouselProps {
    cardSize: CreateCardSize;
    carouselTitle: string;
    carouselTitleCounter?: string;
    carouselMorePath?: string | UrlObject;
    items: CreateCardCarouselItem[];
    isLoading?: boolean;
    limit?: number;
    onCardPlay?: (id: string) => void;
    noResultsText?: string;
    itemBasePath?: string;
    isContest?: boolean;
    isCardPlayable?: boolean;
    contentType: CreateActionType | CreateCardContentTypeWithoutActions;
    isInContainer?: boolean;
    noPadding?: boolean;
    linesWidthArrayGhost?: number[];
    lineHeightGhost?: number;
}

export function CreateCardCarousel({
    cardSize,
    carouselTitle,
    carouselTitleCounter,
    carouselMorePath,
    items,
    limit = DEFAULT_BROWSE_CARD_LIMIT,
    isLoading,
    onCardPlay,
    noResultsText,
    itemBasePath,
    contentType,
    isInContainer,
    noPadding = false,
    linesWidthArrayGhost,
    lineHeightGhost
}: CreateCardCarouselProps) {
    const slicedData = useMemo(() => (items ? items.slice(0, limit) : []), [items, limit]);

    const likeType = useMemo(() => {
        switch (contentType) {
            case 'pack':
                return 'soundPackage';
            case 'curated-set':
                return 'curated';
            case 'sound':
                return 'sound';
            case 'label':
                return 'label';
            case 'contest':
            case 'news':
                return undefined;
            default:
                /* TODO: revisit - the default should probably be undefined too */
                return 'label';
        }
    }, [contentType]);

    const { isLiked, likeDislike } = useCreateLike(likeType);

    const lineHeight = () => {
        if (lineHeightGhost) return lineHeightGhost;
        switch (cardSize) {
            case 'large-square':
                return 20;
            case 'small':
                return 20;
            default:
                return undefined;
        }
    };

    const linesWidthArray = () => {
        if (linesWidthArrayGhost) return linesWidthArrayGhost;
        switch (cardSize) {
            case 'small':
                return [100];
            default:
                return undefined;
        }
    };

    if (isLoading) {
        return <GhostComponent type="cards" cardSize={cardSize} title={carouselTitle} elementsCount={limit} linesWidthArray={linesWidthArray()} lineHeight={lineHeight()} />;
    }

    if (!isLoading && !slicedData.length && noResultsText) {
        return <NoResultsBlock verticalMargins={16}>{noResultsText}</NoResultsBlock>;
    }

    if (!isLoading && !slicedData.length) {
        return (
            <NoResultsBlock hasPadding>{`No ${carouselTitle} Available`}</NoResultsBlock>
        );
    }

    function contestDeadline(date: string) {
        const today = new Date();
        const yyyy = today.getFullYear();
        let mm = today.getMonth() + 1;
        let dd = today.getDate();
        if (dd < 10) dd = 0 + dd;
        if (mm < 10) mm = 0 + mm;
        const formattedToday = `${yyyy}-${mm}-${dd}`;

        const hasExpired = dayjs(date).isBefore(formattedToday);

        if (hasExpired === true) {
            return `Expired ${dayjs(date).tz('America/Los_Angeles').format('MMMM DD, YYYY')}`;
        }

        return `Deadline ${dayjs(date).tz('America/Los_Angeles').format('MMMM DD, YYYY')}`;
    }

    return (
        <CardCarousel title={carouselTitle} titleCounter={carouselTitleCounter} seeMorePath={carouselMorePath} isInContainer={isInContainer} noPadding={noPadding}>
            {slicedData?.map((item) => (
                <CardCreate
                    contentType={contentType}
                    cardSize={cardSize}
                    key={item.id}
                    id={`${item.id}`}
                    title={item.title}
                    approved={item.approved}
                    imageUrl={appendQueryParams(item.coverUrl, { key: 'dw', value: cardSize === 'large' || cardSize === 'large-square' ? 264 : 160 })}
                    imageUrl2x={appendQueryParams(item.coverUrl, { key: 'dw', value: cardSize === 'large' || cardSize === 'large-square' ? 528 : 320 })}
                    label={item.label ?? undefined}
                    onCardPlay={item.isPlayable ? onCardPlay : undefined}
                    description={contentType === 'contest' ? contestDeadline(item.submission_deadline!) : item.description}
                    onFavorite={likeDislike}
                    isFavorite={isLiked(item.id)}
                    shareURL={itemBasePath ? `${itemBasePath}/${encodeURIComponent(item.slug)}` : undefined}
                    link={itemBasePath ? `${itemBasePath}/${encodeURIComponent(item.slug)}` : undefined}
                    hasExtraDemo={item.hasExtraDemo}
                    badge={item.badge}
                    items={items}
                />
            ))}
        </CardCarousel>
    );
}
